import React, { Fragment, useCallback, useState } from 'react';
import { ErrorBoundary } from 'utils/error';
import {
  useDriverDayDetail,
  reviewDriverDay,
  unassignJourney,
  useDriverDayFiltered,
} from 'modules/job-review/service';
import { useParams, Link } from 'react-router-dom';
import { getShipperOrdersSkin } from 'utils/styled/Skins';

import { useComputedValuesOfDriverDay } from 'modules/job-review/detail/computed';

import { Tag, Button, Icon } from '@ontruck/orc';

import JourneyList from './journey-list';
import DriverDayStop from './stop';
import StopVehicle from './stop-vehicle';
import DriverDayStopSeparator from './stop-separator';
import JobReviewActionsTooltip from './actions-tooltip';
import { injectIntl } from 'react-intl';
const t = {
  review_driver_day: 'Mark this driver as reviewed',
  no_review_needed: 'Driver successfully reviewed',
  error: 'Something went wrong',
};

const ErrorFallback = () => <div className="error">{t.error}</div>;

const JobReviewDetail = ({ className, parentUrl, intl }) => {
  const labels = intl.messages.modules.job_review.reasons;
  const t_reasons = {
    no_vehicle_assigned_to_journey: labels.no_vehicle_assigned_to_journey,
    unsuitable_vehicle_type_for_journey: labels.unsuitable_vehicle_type_for_journey,
    problematic_day_for_driver: labels.problematic_day_for_driver,
  };

  const { id } = useParams();

  const [loading, setLoading] = useState(false);

  const { mutate: mutateList } = useDriverDayFiltered({ suspense: false });
  const { driverDay, error, mutate: mutateDetail } = useDriverDayDetail(id, { suspense: false });

  const {
    isFirstStopOfJourney,
    isLastStopOfJourney,
    vehicleByStopIndex,
    shipperOrderIdsPerJourney,
  } = useComputedValuesOfDriverDay(driverDay);

  const { shipper_orders_skin: skins } = getShipperOrdersSkin(driverDay?.journeys);

  const stops = driverDay?.stops;
  const journeys = driverDay?.journeys;
  const driverDayId = driverDay?.id;

  const handleReview = useCallback(async () => {
    try {
      setLoading(true);
      await reviewDriverDay(driverDayId);
      await mutateDetail(
        {
          ...driverDay,
          need_review: false,
        },
        true
      );
      await mutateList();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [driverDay, driverDayId, mutateDetail, mutateList]);

  const handleUnassign = useCallback(
    async (journeyId) => {
      try {
        setLoading(true);
        await unassignJourney(journeyId);
        await mutateDetail();
        await mutateList();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [mutateDetail, mutateList]
  );

  const stopRenderer = (stop, key) => {
    const vehicleForStop = vehicleByStopIndex[key];
    const currentStopHasVehicle = !!vehicleForStop;
    const nextStopHasVehicle = !!vehicleByStopIndex[key + 1];

    const firstStopOfJourney = isFirstStopOfJourney(key, stop.journey_id);
    const lastStopOfJourney = isLastStopOfJourney(key, stop.journey_id);

    const separatorSkin = !lastStopOfJourney ? skins[stop.journey_id] : null;

    return (
      <Fragment key={`${stop.journey_id}-${key}`}>
        {currentStopHasVehicle && <StopVehicle vehicle={vehicleForStop} />}
        <DriverDayStop
          stop={stop}
          stopKey={key + 1}
          isFirst={key === 0 || currentStopHasVehicle}
          isLast={stops?.length - 1 === key || nextStopHasVehicle}
          isFilled={stop.stop_type === 'DROPOFF'}
          skin={skins[stop.journey_id]}
          endsEmpty={lastStopOfJourney}
          startsEmpty={firstStopOfJourney}
          showActions={!!driverDay?.need_review}
          actionsDisabled={loading}
          driverDayId={driverDayId}
          shipperOrderIds={shipperOrderIdsPerJourney[stop.journey_id]}
        />
        {!nextStopHasVehicle && stops?.length - 1 !== key && (
          <DriverDayStopSeparator skin={separatorSkin} isEmpty={lastStopOfJourney} />
        )}
      </Fragment>
    );
  };

  return (
    <div className={className}>
      <div className="header">
        <Link to={parentUrl} className="close_button">
          <Icon glyph="close" size="2rem" color="white" />
        </Link>
      </div>

      {!!error && <ErrorFallback />}

      <div className="driver">
        <div className="container">
          <div className="driver__name">{driverDay?.driver?.contact_name}</div>
          <div className="driver__carrier_name">{driverDay?.driver?.carrier_name}</div>
        </div>
        <div className="">
          {!!driverDay && !!driverDay?.need_review && (
            <Button onClick={handleReview} disabled={loading} text={t.review_driver_day} />
          )}
        </div>
      </div>

      <div className="reasons">
        {driverDay?.reasons?.map((reason) => (
          <Tag
            key={reason}
            text={t_reasons[reason] || reason}
            skin="modernLightGrey"
            weight="bold"
            textTransform="uppercase"
            className="reason"
          />
        ))}
      </div>

      {!!driverDay && !driverDay?.need_review && (
        <div className="success">{t.no_review_needed}</div>
      )}

      <JourneyList
        journeys={journeys}
        skins={skins}
        showActions={!!driverDay?.need_review}
        actionsDisabled={loading}
        shipperOrderIdsPerJourney={shipperOrderIdsPerJourney}
      />

      {stops?.map(stopRenderer)}

      <JobReviewActionsTooltip handleUnassign={handleUnassign} />
    </div>
  );
};

const JobReviewDetailBoundary = (props) => (
  <ErrorBoundary fallback={<ErrorFallback />}>
    <JobReviewDetail {...props} />
  </ErrorBoundary>
);

export default injectIntl(JobReviewDetailBoundary);
